import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  X, CheckCircle2, Circle, ChevronRight,
  Database, Cloud, AlertCircle, Loader2, Eye, EyeOff,
} from 'lucide-react'
import { createAccount, setupMongoDB, startWarehouseSetup, getWarehouseJobStatus, getSetupDefaults, setupUnification } from '../api/accounts'
import type {
  CreateAccountRequest,
  CreateAccountStep,
  AccountStatus,
  SetupMongoDBRequest,
  SetupWarehouseRequest,
  SnowflakeSetupRequest,
  BigQuerySetupRequest,
  WarehouseType,
  CloudEnviron,
  BQCredentialsType,
  TemplateType,
  WarehouseJobResponse,
  UniParams,
  SetupUnificationRequest,
  UniEntityMapping,
} from '../types'

interface CreateAccountWizardProps {
  onClose: () => void
  /** Pass an existing accId to open directly at Step 3 (retrigger warehouse setup). */
  existingAccId?: number
}

const STEPS = [
  { id: 1, label: 'Account Details' },
  { id: 2, label: 'Setup MongoDB' },
  { id: 3, label: 'Setup Data Warehouse' },
  { id: 4, label: 'Unification Config' },
]

const STATUS_OPTIONS: { value: AccountStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'SUSPENDED', label: 'Suspended' },
]

export function CreateAccountWizard({ onClose, existingAccId }: CreateAccountWizardProps) {
  const queryClient = useQueryClient()
  const [currentStep, setCurrentStep] = useState<CreateAccountStep>(existingAccId ? 3 : 1)
  const [createdAccId, setCreatedAccId] = useState<number | null>(existingAccId ?? null)
  const [createdAccountName, setCreatedAccountName] = useState('')

  const [form, setForm] = useState<CreateAccountRequest>({
    accId: 0,
    accountName: '',
    status: 'ACTIVE',
    email: '',
    contactName: '',
    industry: '',
    country: '',
  })
  const [errors, setErrors] = useState<Partial<Record<keyof CreateAccountRequest, string>>>({})

  const mutation = useMutation({
    mutationFn: createAccount,
    onSuccess: (account) => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      setCreatedAccId(account.accId)
      setCreatedAccountName(form.accountName)
      setCurrentStep(2)
    },
    onError: (error: Error) => {
      if (error.message.includes('409') || error.message.toLowerCase().includes('conflict')) {
        setErrors(prev => ({ ...prev, accId: 'Account ID already exists' }))
      }
    },
  })

  const validate = (): boolean => {
    const newErrors: typeof errors = {}
    if (!form.accId || form.accId <= 0) newErrors.accId = 'Account ID is required'
    if (!form.accountName.trim()) newErrors.accountName = 'Account name is required'
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Invalid email address'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleStep1Submit = () => {
    if (!validate()) return
    mutation.mutate(form)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">
            {existingAccId ? `Setup Warehouse — Account ${existingAccId}` : 'Create New Account'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center px-6 py-4 border-b bg-gray-50 flex-shrink-0">
          {STEPS.map((step, idx) => {
            const done = currentStep > step.id
            const active = currentStep === step.id
            return (
              <div key={step.id} className="flex items-center">
                <div className="flex items-center gap-2">
                  {done ? (
                    <CheckCircle2 className="h-6 w-6 text-green-500" />
                  ) : (
                    <Circle
                      className={`h-6 w-6 ${active ? 'text-primary fill-primary/10' : 'text-gray-300'}`}
                    />
                  )}
                  <span className={`text-sm font-medium ${active ? 'text-gray-900' : done ? 'text-green-600' : 'text-gray-400'}`}>
                    {step.label}
                  </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <ChevronRight className="h-4 w-4 text-gray-300 mx-3" />
                )}
              </div>
            )
          })}
        </div>

        {/* Step content — scrollable */}
        <div className="px-6 py-6 overflow-y-auto flex-1">
          {currentStep === 1 && (
            <Step1AccountDetails
              form={form}
              errors={errors}
              onChange={(field, value) => {
                setForm(prev => ({ ...prev, [field]: field === 'accId' ? Number(value) : value }))
                if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }))
              }}
            />
          )}
          {currentStep === 2 && (
            <Step2MongoDB accId={createdAccId} accountName={createdAccountName} />
          )}
          {currentStep === 3 && (
            <Step3DataWarehouse accId={createdAccId} />
          )}
          {currentStep === 4 && (
            <Step4Unification accId={createdAccId} />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <div className="flex gap-3">
            {currentStep === 1 && (
              <button
                onClick={handleStep1Submit}
                disabled={mutation.isPending}
                className="px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
              >
                {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {mutation.isPending ? 'Creating...' : 'Create & Continue'}
              </button>
            )}
            {currentStep === 2 && (
              <button
                onClick={() => setCurrentStep(3)}
                className="px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90"
              >
                Next: Data Warehouse
              </button>
            )}
            {currentStep === 3 && (
              <button
                onClick={() => setCurrentStep(4)}
                className="px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90"
              >
                Next: Unification Config
              </button>
            )}
            {currentStep === 4 && (
              <button
                onClick={onClose}
                className="px-5 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
              >
                Finish
              </button>
            )}
          </div>
        </div>

        {mutation.isError && (
          <div className="mx-6 mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2 flex-shrink-0">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {mutation.error instanceof Error ? mutation.error.message : 'Failed to create account'}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Step 1 ───────────────────────────────────────────────────────────────────

interface Step1Props {
  form: CreateAccountRequest
  errors: Partial<Record<keyof CreateAccountRequest, string>>
  onChange: (field: keyof CreateAccountRequest, value: string) => void
}

function Step1AccountDetails({ form, errors, onChange }: Step1Props) {
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500">Fill in the basic account information.</p>
      <div className="grid grid-cols-2 gap-4">

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Account ID <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min={1}
            value={form.accId || ''}
            onChange={e => onChange('accId', e.target.value)}
            placeholder="e.g. 8500"
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${errors.accId ? 'border-red-400' : 'border-gray-300'}`}
          />
          {errors.accId
            ? <p className="mt-1 text-xs text-red-500">{errors.accId}</p>
            : <p className="mt-1 text-xs text-gray-400">Must be unique — will be rejected if it already exists</p>
          }
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Account Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.accountName}
            onChange={e => onChange('accountName', e.target.value)}
            placeholder="e.g. Acme Corporation"
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${errors.accountName ? 'border-red-400' : 'border-gray-300'}`}
          />
          {errors.accountName && <p className="mt-1 text-xs text-red-500">{errors.accountName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={form.status}
            onChange={e => onChange('status', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
          <input
            type="text"
            value={form.contactName ?? ''}
            onChange={e => onChange('contactName', e.target.value)}
            placeholder="e.g. John Doe"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            value={form.email ?? ''}
            onChange={e => onChange('email', e.target.value)}
            placeholder="e.g. admin@acme.com"
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${errors.email ? 'border-red-400' : 'border-gray-300'}`}
          />
          {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
          <input
            type="text"
            value={form.industry ?? ''}
            onChange={e => onChange('industry', e.target.value)}
            placeholder="e.g. Retail"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
          <input
            type="text"
            value={form.country ?? ''}
            onChange={e => onChange('country', e.target.value)}
            placeholder="e.g. India"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

      </div>
    </div>
  )
}

// ── Step 2: MongoDB Setup ────────────────────────────────────────────────────

interface Step2Props {
  accId: number | null
  accountName: string
}

function Step2MongoDB({ accId, accountName }: Step2Props) {
  const [form, setForm] = useState<SetupMongoDBRequest>({ accountExpire: '', referenceAccId: 0 })
  const [errors, setErrors] = useState<Partial<Record<keyof SetupMongoDBRequest, string>>>({})

  const mutation = useMutation({
    mutationFn: () => setupMongoDB(accId!, form),
  })

  const validate = (): boolean => {
    const e: typeof errors = {}
    if (!form.accountExpire) e.accountExpire = 'Account expiry date is required'
    if (!form.referenceAccId || form.referenceAccId <= 0) e.referenceAccId = 'Reference account ID is required'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleRun = () => {
    if (!validate()) return
    mutation.mutate()
  }

  return (
    <div className="space-y-5">

      {/* Account context banner */}
      {accId && (
        <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
          <span className="text-sm text-green-700">
            Account <strong>{accountName}</strong> created with ID: <strong>{accId}</strong>
          </span>
        </div>
      )}

      <div>
        <p className="text-sm text-gray-600 font-medium mb-1">What this step does</p>
        <p className="text-sm text-gray-500">
          Copies MongoDB setup data from a reference account into <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">cdp_config</code> —
          pricing, settings, communication rules, event mappings, sender profiles, and personalization tokens.
          The reference account's <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">acc_id</code> is replaced with the new account's ID.
        </p>
      </div>

      <div className="space-y-4">

        <div className="grid grid-cols-2 gap-4">

          {/* Reference Account ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reference Account ID <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min={1}
              value={form.referenceAccId || ''}
              onChange={e => {
                setForm(prev => ({ ...prev, referenceAccId: Number(e.target.value) }))
                if (errors.referenceAccId) setErrors(prev => ({ ...prev, referenceAccId: undefined }))
              }}
              disabled={mutation.isSuccess}
              placeholder="e.g. 1001"
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-gray-50 disabled:text-gray-400 ${errors.referenceAccId ? 'border-red-400' : 'border-gray-300'}`}
            />
            {errors.referenceAccId
              ? <p className="mt-1 text-xs text-red-500">{errors.referenceAccId}</p>
              : <p className="mt-1 text-xs text-gray-400">acc_id of an existing account to clone setup data from</p>
            }
          </div>

          {/* Account Expiry */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Expiry Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={form.accountExpire}
              onChange={e => {
                setForm(prev => ({ ...prev, accountExpire: e.target.value }))
                if (errors.accountExpire) setErrors(prev => ({ ...prev, accountExpire: undefined }))
              }}
              disabled={mutation.isSuccess}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-gray-50 disabled:text-gray-400 ${errors.accountExpire ? 'border-red-400' : 'border-gray-300'}`}
            />
            {errors.accountExpire
              ? <p className="mt-1 text-xs text-red-500">{errors.accountExpire}</p>
              : <p className="mt-1 text-xs text-gray-400">Overrides the reference account's expiry date</p>
            }
          </div>

        </div>

        {/* Collections summary */}
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Collections copied from reference account</p>
          </div>
          <div className="grid grid-cols-2 divide-x divide-y divide-gray-100">
            {[
              { name: 'account_pricing', desc: 'Billing & channel rates' },
              { name: 'account_settings', desc: 'Config key-value pairs' },
              { name: 'communication_settings', desc: 'DND, frequency caps' },
              { name: 'event_mapping', desc: 'Event → attribute maps' },
              { name: 'personalized_mappings', desc: 'Campaign tokens' },
              { name: 'sender_emails', desc: 'Approved sender addresses' },
              { name: 'sender_profiles', desc: 'Vendor channel profiles' },
            ].map(c => (
              <div key={c.name} className="px-4 py-2">
                <p className="text-xs font-mono text-gray-700">{c.name}</p>
                <p className="text-xs text-gray-400">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Run button + status */}
      <div className="flex items-center gap-3 pt-1">
        <button
          onClick={handleRun}
          disabled={!accId || mutation.isPending || mutation.isSuccess}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
        >
          {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          <Database className="h-4 w-4" />
          {mutation.isPending ? 'Running Setup...' : mutation.isSuccess ? 'Setup Complete' : 'Run MongoDB Setup'}
        </button>

        {mutation.isSuccess && (
          <span className="flex items-center gap-1.5 text-sm text-green-600">
            <CheckCircle2 className="h-4 w-4" /> Collections created successfully
          </span>
        )}
      </div>

      {mutation.isError && (
        <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-700">Setup failed</p>
            <p className="text-xs text-red-600 mt-0.5">
              {mutation.error instanceof Error ? mutation.error.message : 'An error occurred during MongoDB setup'}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Step 3: Data Warehouse Setup ─────────────────────────────────────────────

const BQ_LOCATIONS = [
  { value: 'asia-south1', label: 'asia-south1 (Mumbai)' },
  { value: 'asia-south2', label: 'asia-south2 (Delhi)' },
  { value: 'asia-southeast1', label: 'asia-southeast1 (Singapore)' },
  { value: 'asia-east1', label: 'asia-east1 (Taiwan)' },
  { value: 'us-central1', label: 'us-central1 (Iowa)' },
  { value: 'us-east1', label: 'us-east1 (S. Carolina)' },
  { value: 'europe-west1', label: 'europe-west1 (Belgium)' },
  { value: 'europe-west2', label: 'europe-west2 (London)' },
  { value: 'australia-southeast1', label: 'australia-southeast1 (Sydney)' },
]

const TEMPLATE_OPTIONS: { value: TemplateType; label: string }[] = [
  { value: 'standard',  label: 'Standard' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'loyalty',   label: 'Loyalty' },
  { value: 'amc',       label: 'AMC' },
  { value: 'aviation',  label: 'Aviation' },
]

const EMPTY_SF: SnowflakeSetupRequest = {
  account: '', username: '', patToken: '', database: '', warehouse: '',
  storageIntegration: '', environ: 'aws', s3Bucket: '',
  azureAccount: '', container: '', loadFrequency: 10, template: 'standard',
}

// Persist the 5 non-secret Snowflake fields the user most often re-enters
const SF_STORAGE_KEY = 'fh_sf_defaults'

type SfStoredDefaults = Pick<SnowflakeSetupRequest, 'account' | 'database' | 'warehouse' | 'storageIntegration' | 's3Bucket'>

function loadSfStoredDefaults(): Partial<SfStoredDefaults> {
  try {
    const raw = localStorage.getItem(SF_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Partial<SfStoredDefaults>) : {}
  } catch {
    return {}
  }
}

function saveSfStoredDefaults(form: SnowflakeSetupRequest) {
  try {
    const { account, database, warehouse, storageIntegration, s3Bucket } = form
    localStorage.setItem(SF_STORAGE_KEY, JSON.stringify({ account, database, warehouse, storageIntegration, s3Bucket }))
  } catch {
    // ignore write errors (e.g. private browsing quota)
  }
}

const EMPTY_BQ: BigQuerySetupRequest = {
  projectId: '', location: 'asia-south1', gcsBucket: '', credentialsType: 'service-account',
  template: 'standard',
}

function Step3DataWarehouse({ accId }: { accId: number | null }) {
  const [warehouseType, setWarehouseType] = useState<WarehouseType>('SNOWFLAKE')
  const [sfForm, setSfForm] = useState<SnowflakeSetupRequest>(() => ({
    ...EMPTY_SF,
    ...loadSfStoredDefaults(),
  }))
  const [bqForm, setBqForm] = useState<BigQuerySetupRequest>(EMPTY_BQ)
  const [sfErrors, setSfErrors] = useState<Partial<Record<keyof SnowflakeSetupRequest, string>>>({})
  const [bqErrors, setBqErrors] = useState<Partial<Record<keyof BigQuerySetupRequest, string>>>({})
  const [showPat, setShowPat] = useState(false)

  /** jobId returned after submitting — drives the polling query */
  const [jobId, setJobId] = useState<string | null>(null)
  /** Current polled job state */
  const [job, setJob] = useState<WarehouseJobResponse | null>(null)

  const derivedSchema = accId ? `acc_${accId}` : 'acc_...'

  // Load non-secret defaults from server to pre-populate form
  const { data: defaults, isLoading: loadingDefaults } = useQuery({
    queryKey: ['setup-defaults'],
    queryFn: getSetupDefaults,
    staleTime: Infinity,
  })

  useEffect(() => {
    if (!defaults) return
    // localStorage values take priority over server defaults for the 5 persisted fields
    const stored = loadSfStoredDefaults()
    setSfForm(prev => ({
      account: stored.account || defaults.snowflake.account,
      username: defaults.snowflake.username,
      patToken: prev.patToken,   // never overwrite a token the user already pasted
      database: stored.database || defaults.snowflake.database,
      warehouse: stored.warehouse || defaults.snowflake.warehouse,
      storageIntegration: stored.storageIntegration || defaults.snowflake.storageIntegration,
      environ: defaults.snowflake.environ,
      s3Bucket: stored.s3Bucket || (defaults.snowflake.s3Bucket ?? ''),
      azureAccount: defaults.snowflake.azureAccount ?? '',
      container: defaults.snowflake.container ?? '',
      loadFrequency: defaults.snowflake.defaultLoadFrequency,
      template: prev.template,
    }))
    setBqForm(prev => ({
      projectId: defaults.bigquery.projectId,
      location: defaults.bigquery.location,
      gcsBucket: defaults.bigquery.gcsBucket,
      credentialsType: defaults.bigquery.credentialsType,
      template: prev.template,
    }))
  }, [defaults])

  // Mutation: submit → returns jobId immediately
  const mutation = useMutation({
    mutationFn: () => {
      const payload: SetupWarehouseRequest =
        warehouseType === 'SNOWFLAKE'
          ? { warehouseType, snowflake: sfForm }
          : { warehouseType, bigquery: bqForm }
      return startWarehouseSetup(accId!, payload)
    },
    onSuccess: (response) => {
      setJobId(response.jobId)
      setJob(response)
    },
  })

  // Polling query: enabled while jobId exists and job is still RUNNING
  const isRunning = job?.status === 'RUNNING'
  const { data: polledJob } = useQuery({
    queryKey: ['warehouse-job', jobId],
    queryFn: () => getWarehouseJobStatus(accId!, jobId!),
    enabled: !!jobId && !!accId && isRunning,
    refetchInterval: isRunning ? 3000 : false,
  })

  useEffect(() => {
    if (polledJob) setJob(polledJob)
  }, [polledJob])

  // Persist the 5 non-secret Snowflake fields to localStorage on every change
  useEffect(() => {
    saveSfStoredDefaults(sfForm)
  }, [sfForm.account, sfForm.database, sfForm.warehouse, sfForm.storageIntegration, sfForm.s3Bucket])

  const validateSnowflake = (): boolean => {
    const e: typeof sfErrors = {}
    if (!sfForm.account.trim()) e.account = 'Required'
    if (!sfForm.username.trim()) e.username = 'Required'
    if (!sfForm.patToken.trim()) e.patToken = 'Required'
    if (!sfForm.database.trim()) e.database = 'Required'
    if (!sfForm.warehouse.trim()) e.warehouse = 'Required'
    if (!sfForm.storageIntegration.trim()) e.storageIntegration = 'Required'
    if (sfForm.environ === 'aws' && !sfForm.s3Bucket?.trim()) e.s3Bucket = 'Required for AWS'
    if (sfForm.environ === 'azure' && !sfForm.azureAccount?.trim()) e.azureAccount = 'Required for Azure'
    if (sfForm.environ === 'azure' && !sfForm.container?.trim()) e.container = 'Required for Azure'
    setSfErrors(e)
    return Object.keys(e).length === 0
  }

  const validateBigQuery = (): boolean => {
    const e: typeof bqErrors = {}
    if (!bqForm.projectId.trim()) e.projectId = 'Required'
    if (!bqForm.gcsBucket.trim()) e.gcsBucket = 'Required'
    setBqErrors(e)
    return Object.keys(e).length === 0
  }

  const handleRun = () => {
    const valid = warehouseType === 'SNOWFLAKE' ? validateSnowflake() : validateBigQuery()
    if (!valid) return
    mutation.mutate()
  }

  const sfField = (key: keyof SnowflakeSetupRequest) => ({
    onChange: (val: string | number) => {
      setSfForm(prev => ({ ...prev, [key]: val }))
      if (sfErrors[key]) setSfErrors(prev => ({ ...prev, [key]: undefined }))
    },
    error: sfErrors[key],
  })

  const bqField = (key: keyof BigQuerySetupRequest) => ({
    onChange: (val: string) => {
      setBqForm(prev => ({ ...prev, [key]: val }))
      if (bqErrors[key]) setBqErrors(prev => ({ ...prev, [key]: undefined }))
    },
    error: bqErrors[key],
  })

  // Disable form inputs once submitted
  const formDisabled = mutation.isPending || !!jobId

  if (loadingDefaults) {
    return (
      <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading configuration defaults...</span>
      </div>
    )
  }

  return (
    <div className="space-y-5">

      {/* Account context banner */}
      {accId && (
        <div className="flex items-center gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
          <CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
          <span className="text-sm text-blue-700">
            Configuring data warehouse for account ID: <strong>{accId}</strong>
            &nbsp;— schema will be <code className="text-xs bg-blue-100 px-1 py-0.5 rounded font-mono">{derivedSchema}</code>
          </span>
        </div>
      )}

      {/* Warehouse type toggle */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Data Warehouse</label>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden w-fit">
          {(['SNOWFLAKE', 'BIGQUERY'] as WarehouseType[]).map(type => (
            <button
              key={type}
              onClick={() => { if (!formDisabled) setWarehouseType(type) }}
              disabled={formDisabled}
              className={`px-5 py-2 text-sm font-medium transition-colors ${
                warehouseType === type
                  ? 'bg-primary text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {type === 'SNOWFLAKE' ? '❄️ Snowflake' : '☁️ BigQuery'}
            </button>
          ))}
        </div>
      </div>

      {/* Template selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Template <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {TEMPLATE_OPTIONS.map(opt => {
            const active = (warehouseType === 'SNOWFLAKE' ? sfForm.template : bqForm.template) === opt.value
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  if (formDisabled) return
                  setSfForm(prev => ({ ...prev, template: opt.value }))
                  setBqForm(prev => ({ ...prev, template: opt.value }))
                }}
                disabled={formDisabled}
                className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors disabled:opacity-50 ${
                  active
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-primary hover:text-primary'
                }`}
              >
                {opt.label}
              </button>
            )
          })}
        </div>
        <p className="mt-1.5 text-xs text-gray-400">
          Selects the schema template set to deploy. <strong>Standard</strong> is the base template; others add industry-specific tables on top.
        </p>
      </div>

      {/* ── Snowflake form ── */}
      {warehouseType === 'SNOWFLAKE' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">

            <FormField
              label="Snowflake Account"
              required
              value={sfForm.account}
              placeholder="e.g. firsthive-v3_poc"
              error={sfErrors.account}
              disabled={formDisabled}
              onChange={sfField('account').onChange as (v: string) => void}
            />
            <FormField
              label="Username"
              required
              value={sfForm.username}
              placeholder="e.g. jdbc_user"
              error={sfErrors.username}
              disabled={formDisabled}
              onChange={sfField('username').onChange as (v: string) => void}
            />
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Programmatic Access Token (PAT) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPat ? 'text' : 'password'}
                  value={sfForm.patToken}
                  onChange={e => {
                    setSfForm(prev => ({ ...prev, patToken: e.target.value }))
                    if (sfErrors.patToken) setSfErrors(prev => ({ ...prev, patToken: undefined }))
                  }}
                  disabled={formDisabled}
                  placeholder="Paste your Snowflake PAT here"
                  className={`w-full px-3 py-2 pr-10 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-gray-50 disabled:text-gray-400 ${sfErrors.patToken ? 'border-red-400' : 'border-gray-300'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPat(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPat ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {sfErrors.patToken
                ? <p className="mt-1 text-xs text-red-500">{sfErrors.patToken}</p>
                : <p className="mt-1 text-xs text-gray-400">Generated in Snowflake under your user profile → Programmatic Access Tokens.</p>
              }
            </div>
            <FormField
              label="Database"
              required
              value={sfForm.database}
              placeholder="e.g. cdp"
              error={sfErrors.database}
              disabled={formDisabled}
              onChange={sfField('database').onChange as (v: string) => void}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Schema (auto-derived)</label>
              <input type="text" value={derivedSchema} readOnly
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 font-mono cursor-not-allowed" />
            </div>
            <FormField
              label="Virtual Warehouse"
              required
              value={sfForm.warehouse}
              placeholder="e.g. V3_POC_WH"
              error={sfErrors.warehouse}
              disabled={formDisabled}
              onChange={sfField('warehouse').onChange as (v: string) => void}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cloud Provider <span className="text-red-500">*</span>
              </label>
              <select value={sfForm.environ} onChange={e => sfField('environ').onChange(e.target.value as CloudEnviron)}
                disabled={formDisabled}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-gray-50">
                <option value="aws">AWS (S3)</option>
                <option value="azure">Azure (Blob Storage)</option>
              </select>
            </div>
            <div className="col-span-2">
              <FormField
                label="Storage Integration"
                required
                value={sfForm.storageIntegration}
                placeholder="e.g. s3_integration"
                error={sfErrors.storageIntegration}
                disabled={formDisabled}
                onChange={sfField('storageIntegration').onChange as (v: string) => void}
              />
              <p className="mt-1 text-xs text-gray-400">Pre-created Snowflake storage integration (infra-level).</p>
            </div>
            {sfForm.environ === 'aws' && (
              <div className="col-span-2">
                <FormField
                  label="S3 Bucket Name"
                  required
                  value={sfForm.s3Bucket ?? ''}
                  placeholder="e.g. fhin-cdp  (no s3:// prefix)"
                  error={sfErrors.s3Bucket}
                  disabled={formDisabled}
                  onChange={sfField('s3Bucket').onChange as (v: string) => void}
                />
              </div>
            )}
            {sfForm.environ === 'azure' && (
              <>
                <FormField
                  label="Azure Storage Account"
                  required
                  value={sfForm.azureAccount ?? ''}
                  placeholder="e.g. myazureaccount"
                  error={sfErrors.azureAccount}
                  disabled={formDisabled}
                  onChange={sfField('azureAccount').onChange as (v: string) => void}
                />
                <FormField
                  label="Container Name"
                  required
                  value={sfForm.container ?? ''}
                  placeholder="e.g. cdp-data"
                  error={sfErrors.container}
                  disabled={formDisabled}
                  onChange={sfField('container').onChange as (v: string) => void}
                />
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Task Load Frequency (minutes)
              </label>
              <input type="number" min={1} max={60} value={sfForm.loadFrequency}
                onChange={e => sfField('loadFrequency').onChange(Number(e.target.value))}
                disabled={formDisabled}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-gray-50" />
              <p className="mt-1 text-xs text-gray-400">Snowflake tasks CRON interval. Default: 10 minutes.</p>
            </div>

          </div>

          {/* Summary */}
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">What will be created in Snowflake</p>
            </div>
            <div className="px-4 py-3 grid grid-cols-3 gap-3 text-xs text-gray-600">
              <div><span className="font-semibold text-gray-700">1 Schema</span><br />
                <span className="text-gray-400 font-mono">{sfForm.database || '<db>'}.{derivedSchema}</span></div>
              <div><span className="font-semibold text-gray-700">9 Stages</span><br />
                <span className="text-gray-400">S3 / Azure paths per data type</span></div>
              <div><span className="font-semibold text-gray-700">14 Tables</span><br />
                <span className="text-gray-400">Customer, events, transactions…</span></div>
              <div><span className="font-semibold text-gray-700">9 Tasks</span><br />
                <span className="text-gray-400">CRON loaders (every {sfForm.loadFrequency}m)</span></div>
              <div><span className="font-semibold text-gray-700">3 Views</span><br />
                <span className="text-gray-400">Customer 360, sessions, metrics</span></div>
            </div>
          </div>
        </div>
      )}

      {/* ── BigQuery form ── */}
      {warehouseType === 'BIGQUERY' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <FormField
                label="GCP Project ID"
                required
                value={bqForm.projectId}
                placeholder="e.g. spark-bigquery-poc"
                error={bqErrors.projectId}
                disabled={formDisabled}
                onChange={bqField('projectId').onChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dataset ID (auto-derived)</label>
              <input type="text" value={derivedSchema} readOnly
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 font-mono cursor-not-allowed" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dataset Location <span className="text-red-500">*</span>
              </label>
              <select value={bqForm.location} onChange={e => bqField('location').onChange(e.target.value)}
                disabled={formDisabled}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-gray-50">
                {BQ_LOCATIONS.map(loc => (
                  <option key={loc.value} value={loc.value}>{loc.label}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <FormField
                label="GCS Bucket Name"
                required
                value={bqForm.gcsBucket}
                placeholder="e.g. fhin-data  (no gs:// prefix)"
                error={bqErrors.gcsBucket}
                disabled={formDisabled}
                onChange={bqField('gcsBucket').onChange}
              />
              <p className="mt-1 text-xs text-gray-400">GCS bucket used by the data pipeline to stage account data.</p>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Authentication Method <span className="text-red-500">*</span>
              </label>
              <select value={bqForm.credentialsType}
                onChange={e => bqField('credentialsType').onChange(e.target.value as BQCredentialsType)}
                disabled={formDisabled}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-gray-50">
                <option value="service-account">Service Account (key file)</option>
                <option value="application-default">Application Default Credentials (GKE Workload Identity)</option>
              </select>
              <p className="mt-1 text-xs text-gray-400">
                {bqForm.credentialsType === 'application-default'
                  ? 'Uses GKE Workload Identity — no key file required on the server.'
                  : 'Service account JSON path is configured server-side — not exposed here.'}
              </p>
            </div>
          </div>

          {/* Summary */}
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">What will be created in BigQuery</p>
            </div>
            <div className="px-4 py-3 grid grid-cols-3 gap-3 text-xs text-gray-600">
              <div><span className="font-semibold text-gray-700">1 Dataset</span><br />
                <span className="text-gray-400 font-mono">{bqForm.projectId || '<project>'}.{derivedSchema}</span></div>
              <div><span className="font-semibold text-gray-700">19 Tables</span><br />
                <span className="text-gray-400">Customer, events, transactions…</span></div>
              <div><span className="font-semibold text-gray-700">8 Views</span><br />
                <span className="text-gray-400">Customer 360, sessions, dedup…</span></div>
            </div>
          </div>
        </div>
      )}

      {/* Run button */}
      {!jobId && (
        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={handleRun}
            disabled={!accId || mutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            <Cloud className="h-4 w-4" />
            {mutation.isPending
              ? 'Starting...'
              : `Run ${warehouseType === 'SNOWFLAKE' ? 'Snowflake' : 'BigQuery'} Setup`}
          </button>
        </div>
      )}

      {mutation.isError && !jobId && (
        <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-700">Failed to start setup</p>
            <p className="text-xs text-red-600 mt-0.5">
              {mutation.error instanceof Error ? mutation.error.message : 'An error occurred'}
            </p>
          </div>
        </div>
      )}

      {/* ── Warehouse live job progress ── */}
      {job && (
        <div className="space-y-3">
          {job.status === 'RUNNING' && (
            <div className="flex items-center gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Loader2 className="h-4 w-4 text-blue-500 animate-spin flex-shrink-0" />
              <span className="text-sm text-blue-700 font-medium">Setup running… polling every 3s</span>
            </div>
          )}
          {job.status === 'COMPLETED' && (
            <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span className="text-sm text-green-700 font-medium">{job.message ?? 'Setup completed successfully'}</span>
            </div>
          )}
          {job.status === 'FAILED' && (
            <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-700">Setup failed</p>
                {job.message && <p className="text-xs text-red-600 mt-0.5 whitespace-pre-wrap">{job.message}</p>}
              </div>
            </div>
          )}
          {job.logLines.length > 0 && (
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-3 py-1.5 bg-gray-800 flex items-center justify-between">
                <span className="text-xs text-gray-400 font-mono">Output</span>
                <span className="text-xs text-gray-500">{job.logLines.length} lines</span>
              </div>
              <div className="bg-gray-900 px-3 py-2 h-48 overflow-y-auto font-mono text-xs text-gray-200 space-y-0.5">
                {job.logLines.map((line, i) => (
                  <div key={i} className="leading-relaxed">{line}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  )
}

// ── Step 4: Unification Config ────────────────────────────────────────────────

const AVAILABLE_FIELDS = [
  { value: 'email',          label: 'Email' },
  { value: 'mobile',         label: 'Mobile' },
  { value: 'full_name',      label: 'Full Name' },
  { value: 'first_name',     label: 'First Name' },
  { value: 'last_name',      label: 'Last Name' },
  { value: 'date_of_birth',  label: 'Date of Birth' },
  { value: 'policy_number',  label: 'Policy Number' },
  { value: 'customer_id',    label: 'Customer ID' },
  { value: 'pan_number',     label: 'PAN Number' },
  { value: 'passport_number',label: 'Passport Number' },
  { value: 'external_id',    label: 'External ID' },
]

const FIELD_LABEL: Record<string, string> = Object.fromEntries(
  AVAILABLE_FIELDS.map(f => [f.value, f.label])
)

const DEFAULT_UNI_PARAMS: UniParams = [
  [['email', 'mobile']],
  [['email']],
  [['mobile']],
]

const PRIORITY_COLORS = [
  'border-l-indigo-500 bg-indigo-50/40',
  'border-l-blue-400 bg-blue-50/40',
  'border-l-slate-400 bg-slate-50/40',
]

function Step4Unification({ accId }: { accId: number | null }) {
  const [groups, setGroups] = useState<UniParams>(DEFAULT_UNI_PARAMS)
  const [error, setError] = useState<string | null>(null)
  const [mappings, setMappings] = useState<UniEntityMapping[]>([])
  /** Tracks which rule set has the custom field input open */
  const [customInput, setCustomInput] = useState<{ gi: number; ri: number; value: string } | null>(null)

  /** Collect all distinct field names defined in the uni_params groups */
  const allUniFields = Array.from(new Set(groups.flatMap(g => g.flatMap(rs => rs))))

  const mutation = useMutation({
    mutationFn: () => {
      const payload: SetupUnificationRequest = { uniParams: groups, entityMappings: mappings }
      return setupUnification(accId!, payload)
    },
  })

  const addMapping = () => {
    const defaultField = allUniFields.find(f => !mappings.some(m => m.uniField === f)) ?? (allUniFields[0] ?? '')
    setMappings(prev => [...prev, {
      uniField: defaultField,
      entity: 'customer',
      collection: 'customer_{acc_id}',
      column: '',
      updateMode: 'SET_IF_NULL',
    }])
  }

  const updateMapping = (idx: number, patch: Partial<UniEntityMapping>) =>
    setMappings(prev => prev.map((m, i) => i === idx ? { ...m, ...patch } : m))

  const removeMapping = (idx: number) =>
    setMappings(prev => prev.filter((_, i) => i !== idx))

  const validate = (): boolean => {
    for (let gi = 0; gi < groups.length; gi++) {
      for (let ri = 0; ri < groups[gi].length; ri++) {
        if (groups[gi][ri].length === 0) {
          setError(`Priority ${gi + 1}, Rule Set ${ri + 1} has no fields — add at least one field or remove the rule set.`)
          return false
        }
      }
    }
    if (groups.length === 0) {
      setError('Add at least one priority group.')
      return false
    }
    setError(null)
    return true
  }

  const handleSave = () => {
    if (!validate()) return
    mutation.mutate()
  }

  // Group mutations
  const addGroup = () =>
    setGroups(prev => [...prev, [['email']]])

  const removeGroup = (gi: number) =>
    setGroups(prev => prev.filter((_, i) => i !== gi))

  // Rule set mutations
  const addRuleSet = (gi: number) =>
    setGroups(prev => prev.map((g, i) => i === gi ? [...g, []] : g))

  const removeRuleSet = (gi: number, ri: number) =>
    setGroups(prev => prev.map((g, i) => i === gi ? g.filter((_, j) => j !== ri) : g))

  // Field mutations
  const addField = (gi: number, ri: number, field: string) => {
    setGroups(prev => prev.map((g, i) =>
      i === gi ? g.map((rs, j) =>
        j === ri ? [...rs, field] : rs
      ) : g
    ))
  }

  const removeField = (gi: number, ri: number, fi: number) => {
    setGroups(prev => prev.map((g, i) =>
      i === gi ? g.map((rs, j) =>
        j === ri ? rs.filter((_, k) => k !== fi) : rs
      ) : g
    ))
  }

  const commitCustomField = (gi: number, ri: number, value: string) => {
    const trimmed = value.trim().toLowerCase().replace(/\s+/g, '_')
    if (trimmed && !groups[gi][ri].includes(trimmed)) {
      addField(gi, ri, trimmed)
    }
    setCustomInput(null)
  }

  const formDisabled = mutation.isPending || mutation.isSuccess

  return (
    <div className="space-y-5">

      {/* Account context banner */}
      {accId && (
        <div className="flex items-center gap-2 px-4 py-3 bg-purple-50 border border-purple-200 rounded-lg">
          <CheckCircle2 className="h-4 w-4 text-purple-500 flex-shrink-0" />
          <span className="text-sm text-purple-700">
            Configuring unification rules for account ID: <strong>{accId}</strong>
          </span>
        </div>
      )}

      <div>
        <p className="text-sm text-gray-600 font-medium mb-1">What this step does</p>
        <p className="text-sm text-gray-500">
          Defines how duplicate customer records are identified and merged.
          Rules are evaluated top-down — higher priority groups represent stronger identity signals.
          Within a group, <span className="font-medium text-gray-600">any</span> rule set can match (OR);
          within a rule set, <span className="font-medium text-gray-600">all</span> fields must match (AND).
        </p>
      </div>

      {/* Priority groups */}
      <div className="space-y-3">
        {groups.map((group, gi) => {
          const colorClass = PRIORITY_COLORS[gi] ?? PRIORITY_COLORS[PRIORITY_COLORS.length - 1]
          return (
            <div key={gi} className={`border-l-4 rounded-lg border border-gray-200 overflow-hidden ${colorClass}`}>

              {/* Group header */}
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200/80">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Priority {gi + 1}
                  </span>
                  {gi === 0 && (
                    <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-600 rounded-full font-medium">
                      Highest
                    </span>
                  )}
                </div>
                {!formDisabled && groups.length > 1 && (
                  <button
                    onClick={() => removeGroup(gi)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove priority group"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Rule sets */}
              <div className="px-4 py-3 space-y-2">
                {group.map((ruleSet, ri) => (
                  <div key={ri}>
                    {/* OR divider between rule sets */}
                    {ri > 0 && (
                      <div className="flex items-center gap-2 my-2">
                        <div className="h-px flex-1 bg-gray-200" />
                        <span className="text-xs font-semibold text-gray-400 px-1">OR</span>
                        <div className="h-px flex-1 bg-gray-200" />
                      </div>
                    )}

                    <div className="flex items-center flex-wrap gap-2">
                      {/* Field chips with AND labels */}
                      {ruleSet.map((field, fi) => (
                        <div key={fi} className="flex items-center gap-1">
                          {fi > 0 && (
                            <span className="text-xs font-semibold text-gray-400">AND</span>
                          )}
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-white border border-gray-300 text-gray-700 shadow-sm">
                            {FIELD_LABEL[field] ?? field}
                            {!formDisabled && (
                              <button
                                onClick={() => removeField(gi, ri, fi)}
                                className="text-gray-400 hover:text-red-500 ml-0.5"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </span>
                        </div>
                      ))}

                      {/* Field picker */}
                      {!formDisabled && (
                        customInput?.gi === gi && customInput?.ri === ri ? (
                          <input
                            autoFocus
                            type="text"
                            value={customInput.value}
                            onChange={e => setCustomInput(prev => prev && ({ ...prev, value: e.target.value }))}
                            onKeyDown={e => {
                              if (e.key === 'Enter') commitCustomField(gi, ri, customInput.value)
                              if (e.key === 'Escape') setCustomInput(null)
                            }}
                            onBlur={() => commitCustomField(gi, ri, customInput.value)}
                            placeholder="field_name (Enter to add)"
                            className="text-xs px-2 py-1 border border-primary rounded-full text-gray-700 bg-white focus:outline-none w-44"
                          />
                        ) : (
                          <select
                            value=""
                            onChange={e => {
                              if (e.target.value === '__custom__') {
                                setCustomInput({ gi, ri, value: '' })
                              } else if (e.target.value) {
                                addField(gi, ri, e.target.value)
                              }
                            }}
                            className="text-xs px-2 py-1 border border-dashed border-gray-300 rounded-full text-gray-500 bg-white hover:border-primary hover:text-primary focus:outline-none cursor-pointer"
                          >
                            <option value="">+ Add field</option>
                            {AVAILABLE_FIELDS.filter(f => !ruleSet.includes(f.value)).map(f => (
                              <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                            <option disabled>──────────</option>
                            <option value="__custom__">Custom field...</option>
                          </select>
                        )
                      )}

                      {/* Remove rule set */}
                      {!formDisabled && group.length > 1 && (
                        <button
                          onClick={() => removeRuleSet(gi, ri)}
                          className="ml-auto text-xs text-gray-400 hover:text-red-500 flex items-center gap-1"
                        >
                          <X className="h-3 w-3" /> Rule Set
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Add rule set */}
                {!formDisabled && (
                  <button
                    onClick={() => addRuleSet(gi)}
                    className="mt-2 text-xs text-primary hover:underline font-medium"
                  >
                    + Add Rule Set (OR)
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Add priority group */}
      {!formDisabled && (
        <button
          onClick={addGroup}
          className="w-full py-2 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-500 hover:border-primary hover:text-primary transition-colors"
        >
          + Add Priority Group
        </button>
      )}

      {/* Entity Mappings */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 bg-amber-50 border-b border-gray-200">
          <div>
            <p className="text-sm font-semibold text-amber-800">Uni Field → Entity Mappings</p>
            <p className="text-xs text-amber-600 mt-0.5">
              Applied automatically on every event — no derivation rule needed per event.
            </p>
          </div>
          {!formDisabled && (
            <button
              onClick={addMapping}
              disabled={allUniFields.length === 0}
              className="text-xs px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-40 transition-colors"
            >
              + Add Mapping
            </button>
          )}
        </div>

        {mappings.length === 0 ? (
          <p className="text-xs text-gray-400 italic text-center py-5">
            No mappings configured — add at least one to auto-update customer fields from uni data.
          </p>
        ) : (
          <div className="divide-y divide-gray-100">
            {/* Header row */}
            <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 px-4 py-2 bg-gray-50 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
              <span>Uni Field</span>
              <span>Customer Column</span>
              <span>Update Mode</span>
              <span />
            </div>
            {mappings.map((m, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 items-center px-4 py-2.5">
                {/* Uni field */}
                <select
                  value={m.uniField}
                  onChange={e => updateMapping(idx, { uniField: e.target.value })}
                  disabled={formDisabled}
                  className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 disabled:opacity-50"
                >
                  {allUniFields.map(f => <option key={f} value={f}>{f}</option>)}
                </select>

                {/* Column */}
                <input
                  value={m.column}
                  onChange={e => updateMapping(idx, { column: e.target.value })}
                  disabled={formDisabled}
                  placeholder="e.g. contact_email"
                  className="text-xs font-mono border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400 disabled:opacity-50"
                />

                {/* Update mode */}
                <select
                  value={m.updateMode}
                  onChange={e => updateMapping(idx, { updateMode: e.target.value })}
                  disabled={formDisabled}
                  className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 disabled:opacity-50"
                >
                  <option value="SET_IF_NULL">SET_IF_NULL</option>
                  <option value="SET">SET</option>
                  <option value="MAX_VALUE">MAX_VALUE</option>
                  <option value="MIN_VALUE">MIN_VALUE</option>
                </select>

                {/* Remove */}
                {!formDisabled && (
                  <button
                    onClick={() => removeMapping(idx)}
                    className="text-gray-300 hover:text-red-500 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Validation error */}
      {error && (
        <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700">{error}</p>
        </div>
      )}

      {/* Save button */}
      <div className="flex items-center gap-3 pt-1">
        <button
          onClick={handleSave}
          disabled={!accId || formDisabled}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
        >
          {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          <Database className="h-4 w-4" />
          {mutation.isPending ? 'Saving...' : mutation.isSuccess ? 'Saved' : 'Save Unification Config'}
        </button>
        {mutation.isSuccess && (
          <span className="flex items-center gap-1.5 text-sm text-green-600">
            <CheckCircle2 className="h-4 w-4" /> Configuration saved successfully
          </span>
        )}
      </div>

      {mutation.isError && (
        <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-700">Failed to save configuration</p>
            <p className="text-xs text-red-600 mt-0.5">
              {mutation.error instanceof Error ? mutation.error.message : 'An error occurred'}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Shared helpers ────────────────────────────────────────────────────────────

interface FormFieldProps {
  label: string
  value: string
  placeholder?: string
  error?: string
  disabled?: boolean
  required?: boolean
  onChange: (value: string) => void
}

function FormField({ label, value, placeholder, error, disabled, required, onChange }: FormFieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-gray-50 disabled:text-gray-400 ${error ? 'border-red-400' : 'border-gray-300'}`}
      />
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  )
}
